import { useState } from "react";
import { Link } from "react-router-dom";
import { shortDate } from "@/lib/api";

const shortTitle = (t) => t.split(":")[0];

const Badge = ({ n, ev }) => {
  if (ev.coming_soon) return <span className="rounded-full border border-dashed border-[#C9A24B] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wider text-[#A8832F] sm:px-3 sm:text-[10px]">Coming soon</span>;
  if (n.sold_out) return <span className="rounded-full bg-[#E5DACA] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#6B6157]">Sold out</span>;
  return <span className="rounded-full bg-[#141210] px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[#F3DDA4]">Book</span>;
};

const DateRow = ({ ev, n }) => {
  const d = shortDate(n.date);
  const where = [n.city, n.venue].filter(Boolean).join(" · ") || ev.location;
  return (
    <Link to={`/events/${ev.slug}?night=${n.id}#book`} data-testid={`date-row-${n.id}`}
      className="group flex items-center gap-4 rounded-lg border border-[#E5DACA] bg-white p-3 transition-shadow duration-300 hover:shadow-[0_12px_30px_-14px_rgba(60,40,10,0.3)] sm:gap-6 sm:p-4">
      <div className="w-[60px] shrink-0 rounded-md bg-[#141210] py-2 text-center sm:w-20">
        <span className="block text-[10px] font-extrabold uppercase tracking-widest text-[#D9B864]">{d.weekday}</span>
        <span className="block font-display text-2xl leading-tight text-white sm:text-3xl">{d.day}</span>
        <span className="block text-[10px] font-bold uppercase tracking-widest text-[#BDB3A5]">{d.month} {String(d.year).slice(2)}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#A8832F]">{n.label || "Show night"}</p>
        <h3 className="mt-0.5 line-clamp-2 font-display text-base leading-snug text-[#1F1B17] group-hover:text-[#8C6A22] sm:text-xl">{shortTitle(ev.title)}</h3>
        <p className="truncate text-xs text-[#6B6157] sm:text-sm">{where}</p>
      </div>
      <div className="shrink-0"><Badge n={n} ev={ev} /></div>
    </Link>
  );
};

export const UpcomingDates = ({ events }) => {
  const [filter, setFilter] = useState("all");
  const rows = events
    .filter((ev) => filter === "all" || ev.slug === filter)
    .flatMap((ev) => ev.nights.map((n) => ({ ev, n })))
    .sort((a, b) => a.n.date.localeCompare(b.n.date));
  const chip = (id, label) => (
    <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`dates-filter-${id}`}
      className={`whitespace-nowrap rounded-full border px-4 py-2 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${filter === id ? "border-[#141210] bg-[#141210] text-[#F3DDA4]" : "border-[#E5DACA] bg-white text-[#4A423A] hover:border-[#C9A24B]"}`}>{label}</button>
  );
  return (
    <div data-testid="upcoming-dates">
      {events.length > 1 && (
        <div className="-mx-5 mb-6 flex gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0">
          {chip("all", "All dates")}
          {events.map((ev) => chip(ev.slug, shortTitle(ev.title)))}
        </div>
      )}
      <div className="space-y-3">
        {rows.map(({ ev, n }) => <DateRow key={`${ev.id}-${n.id}`} ev={ev} n={n} />)}
        {!rows.length && <p className="py-8 text-[#6B6157]" data-testid="no-dates-message">New dates are being poured. Check back soon.</p>}
      </div>
    </div>
  );
};
